import {
  AnalyticsFieldName,
  ProfileAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { ModalPath } from "@/constants/Navigation";
import { SafetyPlanTestID } from "@/constants/TestID";
import { useAnalytics } from "@/services/Analytics";
import { useAppUserSelector } from "@/store/reducers/appUser";
import LocalEmsPhoneSection from "@/ui/components/profile/safetyPlan/LocalEmsPhoneSection";
import RiskOfSelfHarmSection from "@/ui/components/profile/safetyPlan/RiskOfSelfHarmSection";
import SafetyPlanSection from "@/ui/components/profile/safetyPlan/SafetyPlanSection";
import type { LinkType } from "@/ui/components/profile/safetyPlan/SafetyPlanSection";
import SupportPeopleSection from "@/ui/components/profile/safetyPlan/SupportPeopleSection";
import { Text } from "@/ui/core";
import { VStack } from "@/ui/layout";
import {
  getIllustrationIcon,
  IllustrationName,
} from "@/ui/theme/icons/illustrations/groupClasses";
import { ColorToken } from "@/ui/theme/tokens";

interface PersonalSafetyPlanProps {
  completedOn: string;
  copingSkills?: string[];
  imminentRiskOfSelfHarm: string;
  localEmsPhone: string;
  mostImportantReasons: string;
  otherWarningSigns: string;
  selectedUserId: string;
  supportPeople?: SafetyPlanSupportPerson[];
  title: string;
  warningSigns: string[];
}

const ShieldSize = 56;

const PersonalSafetyPlan = ({
  completedOn,
  copingSkills,
  imminentRiskOfSelfHarm,
  localEmsPhone,
  mostImportantReasons,
  otherWarningSigns,
  selectedUserId,
  supportPeople,
  title,
  warningSigns,
}: PersonalSafetyPlanProps) => {
  const { externalId: appUserId } = useAppUserSelector();
  const { track } = useAnalytics();
  const trackLinkPressed = ({
    linkType,
    text,
  }: {
    linkType: LinkType;
    text: string;
  }) => {
    track(ProfileAnalyticsEvent.SafetyPlanLinkPressed, {
      [AnalyticsFieldName.AppUser]: appUserId,
      [AnalyticsFieldName.Component]: PersonalSafetyPlan.name,
      [AnalyticsFieldName.LinkType]: linkType,
      [AnalyticsFieldName.SelectedUser]: selectedUserId,
      [AnalyticsFieldName.Source]: ModalPath.SafetyPlanDetails,
      [AnalyticsFieldName.Text]: text,
    });
  };

  const ShieldIllustration = getIllustrationIcon({
    height: ShieldSize,
    iconName: IllustrationName.Shield,
    width: ShieldSize,
  });

  return (
    <VStack gap={24}>
      <VStack gap={18}>
        {ShieldIllustration}
        <VStack gap={6}>
          <Text testID={SafetyPlanTestID.SafetyPlanTitle} variant="h5">
            {title}
          </Text>
          <Text
            color={ColorToken.NeutralSecondary}
            testID={SafetyPlanTestID.CompletedOn}
            variant="caption"
          >
            {completedOn}
          </Text>
        </VStack>
      </VStack>
      <LocalEmsPhoneSection
        localEmsPhone={localEmsPhone}
        onLinkPress={(text: string) => {
          trackLinkPressed({
            linkType: "tel",
            text,
          });
        }}
      />
      <SafetyPlanSection
        shouldPrefixWithBullet
        statements={warningSigns}
        testID={SafetyPlanTestID.WarningSigns}
        title="List three warning signs that you are in a crisis"
      />
      <SafetyPlanSection
        statements={[otherWarningSigns]}
        testID={SafetyPlanTestID.OtherWarningSigns}
        title="Other warning signs"
      />
      <SafetyPlanSection
        shouldPrefixWithBullet
        statements={copingSkills}
        testID={SafetyPlanTestID.CopingSkills}
        title="Name at least three internal coping skills"
      />
      <SafetyPlanSection
        statements={[mostImportantReasons]}
        testID={SafetyPlanTestID.MostImportantReasons}
        title="The most important reason to be alive (/recovering)"
      />
      <SupportPeopleSection
        onLinkPress={(text: string) => {
          trackLinkPressed({
            linkType: "tel",
            text,
          });
        }}
        supportPeople={supportPeople}
      />
      <RiskOfSelfHarmSection
        onLinkPress={(text: string) => {
          trackLinkPressed({
            linkType: "tel",
            text,
          });
        }}
      />
      <SafetyPlanSection
        statements={[imminentRiskOfSelfHarm]}
        testID={SafetyPlanTestID.ImminentRiskOfSelfHarm}
        title="If I am at imminent risk of hurting myself, my plan is to"
      />
    </VStack>
  );
};

export type { PersonalSafetyPlanProps };
export default PersonalSafetyPlan;
